import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useDrawerResize — reusable hook for drag-to-resize analytics drawers.
 *
 * @param {Object}  opts
 * @param {number}  opts.minWidth      – minimum drawer width  (default 400)
 * @param {number}  opts.maxWidth      – maximum drawer width  (default 800)
 * @param {number}  opts.defaultWidth  – initial width          (default 600)
 * @param {string}  opts.storageKey    – sessionStorage key     (default 'analyticsDrawerWidth')
 *
 * @returns {{ drawerWidth: number, isDragging: boolean, dragHandleProps: object }}
 *   - drawerWidth   : current pixel width
 *   - isDragging    : whether the user is actively dragging
 *   - dragHandleProps: spread these on the drag-handle element
 */
const useDrawerResize = ({
    minWidth = 400,
    maxWidth = 800,
    defaultWidth = 600,
    storageKey = 'analyticsDrawerWidth',
} = {}) => {
    // ── persisted width ──────────────────────────────────────
    const [drawerWidth, setDrawerWidth] = useState(() => {
        try {
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                const parsed = parseInt(stored, 10);
                if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) return parsed;
            }
        } catch { /* SSR / permission guard */ }
        return defaultWidth;
    });

    const [isDragging, setIsDragging] = useState(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    // ── persist to sessionStorage ────────────────────────────
    useEffect(() => {
        try { sessionStorage.setItem(storageKey, String(drawerWidth)); } catch { /* noop */ }
    }, [drawerWidth, storageKey]);

    // ── mouse handlers (attached to window while dragging) ──
    const handleMouseMove = useCallback((e) => {
        e.preventDefault();
        // Drawer opens from the RIGHT, so dragging left = bigger
        const delta = startXRef.current - e.clientX;
        const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta));
        setDrawerWidth(newWidth);
    }, [minWidth, maxWidth]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, []);

    // attach / detach global listeners while dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // ── drag handle props ────────────────────────────────────
    const onMouseDown = useCallback((e) => {
        e.preventDefault();
        startXRef.current = e.clientX;
        startWidthRef.current = drawerWidth;
        setIsDragging(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [drawerWidth]);

    const dragHandleProps = {
        onMouseDown,
        style: {
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            zIndex: 10,
            // subtle visual indicator
            background: isDragging
                ? 'rgba(99,102,241,0.35)'
                : 'transparent',
            transition: isDragging ? 'none' : 'background 0.2s',
        },
        onMouseEnter: (e) => {
            if (!isDragging) e.currentTarget.style.background = 'rgba(99,102,241,0.18)';
        },
        onMouseLeave: (e) => {
            if (!isDragging) e.currentTarget.style.background = 'transparent';
        },
        role: 'slider',
        'aria-label': 'Resize analytics drawer',
        'aria-valuemin': minWidth,
        'aria-valuemax': maxWidth,
        'aria-valuenow': drawerWidth,
        tabIndex: 0,
    };

    return { drawerWidth, isDragging, dragHandleProps };
};

export default useDrawerResize;
